import { Fragment, useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Document,
  Page,
  Text,
  View,
  pdf, // ✅ Added for blob generation
} from "@react-pdf/renderer";
import styles from "./PDFReportViewer.module.css";
import styled from "styled-components";
import Button from "../../components/Button";
import { registerPoppins } from "../../fonts/registerFonts";
import backIcon from "../../assets/back_icon.svg";
import AntRadioGroup from "../../components/Ant/AntRadioGroup/AntRadioGroup";
import { useTranslation } from "react-i18next";
import { getLocalizedDate } from "../../functions/localization";
import { Pagination } from "antd";
import {
  PDF_PREVIEW_ROWS,
  getPdfPreviewPageCount,
  formatPdfFooter,
  formatPdfTotal,
  getPdfTotalsRows,
} from "./reportExport";
import {
  computeVariableColumnWidths,
  PDF_PAGE_CONTENT_WIDTH,
  PDF_PAGE_PADDING,
  CELL_PADDING_RIGHT,
} from "./pdfReportGeneration/pdfColumnWidths";

registerPoppins();

const ROWS_PER_PDF_PAGE = 40;

const MyView = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 30px 20px;
  height: 100%;
  max-height: 1024px;
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 75px;
  min-height: 75px;
  border-bottom: 1px solid #a7bbcf;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  width: 304px;
  min-width: 304px;
`;

const BackButton = styled.img`
  cursor: pointer;
  width: 30px;
  height: 30px;
  margin-right: 20px;
`;

const ReportTitle = styled.p`
  margin: 0;
  font-family: "Poppins";
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 33px;
  color: #454b60;
  min-width: 160px;
  width: 160px;
`;

const PDFReportViewer = ({
  user,
  title,
  headings,
  data,
  collectionSequence,
  onBack,
  columnSums,
  columnAverages,
  selectionRange,
  showAVGRow,
  showSUMRow,
  headingsSpacing,
  dataSpacing,
  defaultFontSize,
}) => {
  const { t } = useTranslation("pdfReportViewer");

  // "Equal Widths": the page's usable width (752pt) shared evenly. JFCRX-7651:
  // this was 760, 8pt more than the page has.
  const columnWidth = PDF_PAGE_CONTENT_WIDTH / headings.length;

  const [columnWidthTypeRadioValue, setColumnWidthTypeRadioValue] = useState(1);
  const [fontSizeRadioValue, setFontSizeRadioValue] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 2 : 1,
  );
  const [headingsAndDataFontSize, setHeadingsAndDataFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 8 : 10,
  );
  const [sumAndAvgFontSize, setSumAndAvgFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 10 : 12,
  );

  let allowCustomWidths =
    headingsSpacing?.length === headings.length &&
    dataSpacing?.length === headings.length;

  useEffect(() => {
    if (allowCustomWidths) {
      setColumnWidthTypeRadioValue(3);
    }
  }, []);

  /* JFCRX-6738 (folded in from JFCRX-6794). CONTRACT: `data` is a flat array of
   * ROWS. It is never an array of groups, and this component does not accept a
   * `groupedField` prop -- callers flatten before handing the data over, so
   * the widths are measured from the same rows the document draws.
   *
   * JFCRX-7651. The width arithmetic that stood here (a flat 6.4px per
   * character, capped at 200, then every column scaled by one factor to fit
   * 758pt) is the defect JFCRX-6733 fixed for Production: when the content
   * was wider than the page, every column shrank below its content, and
   * react-pdf drew unbreakable values into the next column. This viewer now
   * uses the same module, pdfColumnWidths.js, which pins each column at its
   * longest unbreakable token (up to MAX_CELL_WIDTH, 200pt; a longer token
   * still overlaps, and if even the floors cannot fit, every column is scaled
   * together), sums the widths to the page's 752pt, and measures at the
   * current font size -- hence headingsAndDataFontSize in the dependency
   * list. A null or undefined cell measures as empty text there
   * (JFCRX-7643).
   *
   * JFCRX-7652. The Sum and Average rows the download will draw are measured
   * too, at their own font size, so each column also fits its total. The
   * preview uses the same widths, so it lays out exactly like the download. */
  const variableColumnWidthsArray = useMemo(
    () =>
      computeVariableColumnWidths({
        headings,
        data,
        fontSize: headingsAndDataFontSize,
        totalsRows: getPdfTotalsRows({
          showSUMRow,
          columnSums,
          showAVGRow,
          columnAverages,
        }),
        totalsFontSize: sumAndAvgFontSize,
      }),
    [
      headings,
      data,
      headingsAndDataFontSize,
      showSUMRow,
      columnSums,
      showAVGRow,
      columnAverages,
      sumAndAvgFontSize,
    ],
  );

  let startDate = selectionRange?.startDate
    ? getLocalizedDate(
        selectionRange.startDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;
  let endDate = selectionRange?.endDate
    ? getLocalizedDate(
        selectionRange.endDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;

  const [currentPage, setCurrentPage] = useState(0);
  const previewData = useMemo(
    () =>
      data.slice(
        currentPage * PDF_PREVIEW_ROWS,
        (currentPage + 1) * PDF_PREVIEW_ROWS,
      ),
    [data, currentPage],
  );
  const fontSize = defaultFontSize === "small" ? 8 : 10;

  /* JFCRX-7643. The preview is one ten-row document per pager page, so
   * react-pdf's own counter inside it always reads "1 / 1". The preview
   * footer states the preview position instead, counted the same way the
   * pager counts. The download passes no label and keeps real page numbers. */
  const previewFooterLabel = t("previewPageLabel", {
    page: currentPage + 1,
    total: getPdfPreviewPageCount(data.length),
  });

  const createPDFDocument = ({
    title,
    headings,
    data,
    columnWidth,
    fontSize,
    startDate,
    endDate,
    isDownload = false,
    footerLabel,
  }) => {
    const pages = [];

    /* The width of a data cell in column `i` for the selected width mode. The
     * Sum and Average cells use it too (JFCRX-7652), so a total sits under its
     * own column in every mode, not only in Variable Widths. */
    const dataCellWidth = (i) => {
      if (columnWidthTypeRadioValue === 1) return variableColumnWidthsArray[i];
      if (columnWidthTypeRadioValue === 3) return dataSpacing[i].columnWidth;
      return columnWidth;
    };

    /* JFCRX-7652. Only totals that have a column are drawn, matching what the
     * width calculation measures. An entry past the last heading would be a
     * Text with no width, and flexShrink would squeeze the whole row off its
     * columns. */
    const totalsCells = (totals) =>
      totals.slice(0, headings.length).map((total, i) => (
        <Text
          key={i}
          style={{
            width: dataCellWidth(i),
            fontSize: sumAndAvgFontSize,
            paddingRight: CELL_PADDING_RIGHT,
          }}
        >
          {formatPdfTotal(total)}
        </Text>
      ));

    const headerView = (
      <View
        fixed
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          fontSize: 10,
          fontWeight: 500,
          marginBottom: 10,
        }}
      >
        <Text>{title}</Text>
        <Text>{startDate && endDate ? `${startDate} - ${endDate}` : ""}</Text>
      </View>
    );

    const headingsRow = (
      <View
        fixed
        style={{
          flexDirection: "row",
          borderBottom: "2px solid #089BAB",
          paddingVertical: 12,
        }}
      >
        {headings.map((heading, i) => {
          let width = columnWidth;
          if (columnWidthTypeRadioValue === 1)
            width = variableColumnWidthsArray[i];
          else if (columnWidthTypeRadioValue === 3)
            width = headingsSpacing[i].columnWidth;

          return (
            <Text
              key={i}
              style={{
                width,
                fontSize: headingsAndDataFontSize,
                fontWeight: 500,
                paddingRight: CELL_PADDING_RIGHT,
              }}
            >
              {heading}
            </Text>
          );
        })}
      </View>
    );

    /* JFCRX-7643. At least one page, so a report with no rows still shows
     * its header, headings and footer rather than a blank viewer. */
    const pageCount = Math.max(1, Math.ceil(data.length / ROWS_PER_PDF_PAGE));
    for (let p = 0; p < pageCount; p += 1) {
      const i = p * ROWS_PER_PDF_PAGE;
      const pageRows = data.slice(i, i + ROWS_PER_PDF_PAGE);
      const isLastPage = p === pageCount - 1;

      pages.push(
        <Page
          key={i}
          size="LETTER"
          orientation="landscape"
          style={{ padding: PDF_PAGE_PADDING, fontFamily: "Poppins" }}
        >
          {headerView}
          {headingsRow}
          {pageRows.map((row, rIdx) => (
            <View
              key={rIdx}
              style={{
                flexDirection: "row",
                backgroundColor: rIdx % 2 ? "#EBEDF8" : "#FFFFFF",
                paddingVertical: 5,
              }}
            >
              {row.map((cell, cIdx) => (
                <Text
                  key={cIdx}
                  style={{
                    width: dataCellWidth(cIdx),
                    fontSize: headingsAndDataFontSize,
                    paddingRight: CELL_PADDING_RIGHT,
                  }}
                >
                  {/* JFCRX-7651. Drawn as given. Dashes used to be
                      swapped for soft hyphens, and textkit drops the dash
                      at a soft-hyphen break ("50111-0788" / "01"); the
                      width module also measures the text as given.
                      Production has drawn cells as given since e8544c712. */}
                  {cell}
                </Text>
              ))}
            </View>
          ))}
          <Text
            fixed
            style={{
              position: "absolute",
              bottom: 6,
              width: "100%",
              textAlign: "center",
              fontSize: 10,
            }}
            render={({ pageNumber, totalPages }) =>
              formatPdfFooter({ footerLabel, pageNumber, totalPages })
            }
          />

          {/* SUM/AVG rows (optional) */}
          {isDownload && isLastPage && (
            <Fragment>
              {showSUMRow && (
                <>
                  <Text
                    style={{
                      fontSize: sumAndAvgFontSize,
                      fontWeight: 500,
                      color: "#089BAB",
                      paddingTop: 12,
                    }}
                  >
                    {t("sumCol")}
                  </Text>
                  <View style={{ flexDirection: "row", paddingTop: 10 }}>
                    {totalsCells(columnSums)}
                  </View>
                </>
              )}
              {showAVGRow && (
                <>
                  <Text
                    style={{
                      fontSize: sumAndAvgFontSize,
                      fontWeight: 500,
                      color: "#089BAB",
                      paddingTop: 12,
                    }}
                  >
                    {t("averageCol")}
                  </Text>
                  <View style={{ flexDirection: "row", paddingTop: 10 }}>
                    {totalsCells(columnAverages)}
                  </View>
                </>
              )}
            </Fragment>
          )}
        </Page>,
      );
    }

    return <Document title={title}>{pages}</Document>;
  };

  const previewDoc = useMemo(() => {
    return createPDFDocument({
      title,
      headings,
      data: previewData,
      columnWidth,
      fontSize,
      startDate,
      endDate,
      isDownload: false,
      footerLabel: previewFooterLabel,
    });
  }, [
    title,
    headings,
    previewData,
    previewFooterLabel,
    columnWidth,
    fontSize,
    startDate,
    endDate,
    columnWidthTypeRadioValue,
    headingsAndDataFontSize,
    variableColumnWidthsArray,
  ]);

  // ✅ New async download function
  const handleDownload = async () => {
    const fullDocInstance = createPDFDocument({
      title,
      headings,
      data,
      columnWidth,
      fontSize,
      startDate,
      endDate,
      isDownload: true,
    });

    const asPdf = pdf([]);
    asPdf.updateContainer(fullDocInstance);
    const blob = await asPdf.toBlob();
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_")}_Report.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MyView>
      <Header>
        <TitleGroup>
          <BackButton src={backIcon} onClick={onBack} />
          <ReportTitle>{t("title")}</ReportTitle>
          <AntRadioGroup
            values={allowCustomWidths ? [1, 2, 3] : [1, 2]}
            labels={
              allowCustomWidths
                ? [
                    t("variableColumnLabel"),
                    t("equalColumnLabel"),
                    t("customColumnLabel"),
                  ]
                : [t("variableColumnLabel"), t("equalColumnLabel")]
            }
            radioGroupClass="ant-radioGroup-000"
            onChange={(e) => setColumnWidthTypeRadioValue(e.target.value)}
            value={columnWidthTypeRadioValue}
          />
          <div className={styles.PDFReportViewer__fontSizeRadioGroupContainer}>
            <AntRadioGroup
              values={[1, 2]}
              labels={[t("largeFontLabel"), t("smallFontLabel")]}
              radioGroupClass="ant-radioGroup-000"
              onChange={(e) => {
                const value = e.target.value;
                setFontSizeRadioValue(value);
                setHeadingsAndDataFontSize(value === 1 ? 10 : 8);
                setSumAndAvgFontSize(value === 1 ? 12 : 10);
              }}
              value={fontSizeRadioValue}
            />
          </div>
        </TitleGroup>

        {/* ✅ Button triggers full PDF generation on demand */}
        <Button
          onClick={handleDownload}
          labelName="Download PDF"
          isPrimary={true}
        >
          Download PDF
        </Button>
      </Header>

      {/* JFCRX-7643. The plugin toolbar's page count can only describe the
          ten-row blob on screen, so it is hidden in the preview. */}
      <PDFViewer width="100%" height="86%" showToolbar={false}>
        {previewDoc}
      </PDFViewer>

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <Pagination
          defaultCurrent={1}
          total={data.length}
          pageSize={PDF_PREVIEW_ROWS}
          current={currentPage + 1}
          onChange={(page) => setCurrentPage(page - 1)}
          showSizeChanger={false}
        />
      </div>
    </MyView>
  );
};

export default PDFReportViewer;
